/**
 * component-button-multistate.js
 * Handles the "In Your Cart" and "Sold Out" states for the Add to Cart button globally (PDP & PLP).
 * Cart membership is seeded from window.cartVariantIds (layout/theme.liquid) and kept current from
 * PUB_SUB_EVENTS.cartUpdate payloads. The only network read is one /cart.js on back/forward-cache restore.
 */
(function () {
    const SELECTORS = {
        BUTTON: '.glymed-cta-button, .complementary-products__add-to-cart',
        IN_CART_CLASS: 'glymed-cta-button--in-cart',
        FORM: 'form',
        VARIANT_INPUT: 'input[name="id"]',
        VARIANT_PICKERS: 'variant-radios, variant-selects, .product-variant-picker'
    };

    const seeded = Array.isArray(window.cartVariantIds);
    let cartVariantIds = new Set(seeded ? window.cartVariantIds.map(Number) : []);

    function init() {
        window.updateGlymedButtonStates = (refresh = false) => (refresh ? refreshFromServer() : updateButtonStates());

        if (seeded) {
            updateButtonStates();
        } else {
            refreshFromServer();
        }

        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
            subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
                if (!applyCartEvent(event)) return;
                setTimeout(updateButtonStates, 100);
            });

            subscribe(PUB_SUB_EVENTS.variantChange, () => {
                setTimeout(updateButtonStates, 100);
            });
        }

        setupMutationObservers();
        setupChangeListeners();

        document.addEventListener('click', handleCartRedirect, true);

        window.addEventListener('pageshow', (event) => {
            if (event.persisted) refreshFromServer();
        });
    }

    // Returns true when the event changed cart membership.
    // /cart/change and /cart/update responses carry the full item list; /cart/add responses and the
    // PDP cart-sync publish only the affected variant and its resulting line quantity.
    function applyCartEvent(event) {
        if (!event) return false;

        const items = event.cartData && event.cartData.items;
        if (Array.isArray(items)) {
            cartVariantIds = new Set(items.map((item) => Number(item.variant_id)));
            return true;
        }

        // Error events carry no cart state, except the PDP cart-sync rollback, which reports the
        // quantity the cart really has for its variant
        if (event.isError && typeof event.quantity !== 'number') return false;

        const variantId = parseInt(event.productVariantId || (event.cartData && event.cartData.variant_id));
        if (!variantId) return false;

        const quantity = event.quantity ?? (event.cartData && event.cartData.quantity) ?? 1;
        if (quantity > 0) {
            cartVariantIds.add(variantId);
        } else {
            cartVariantIds.delete(variantId);
        }
        return true;
    }

    async function refreshFromServer() {
        const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
        try {
            const res = await fetch(`${cartUrl}.js`);
            if (typeof CartThrottle !== 'undefined' && CartThrottle.checkRefresh(res)) return;
            if (!res.ok) return;
            const cart = await res.json();
            cartVariantIds = new Set(cart.items.map((item) => Number(item.variant_id)));
            updateButtonStates();
        } catch (err) {
            console.error('[GlyMed Button] Error fetching cart:', err);
        }
    }

    function setupMutationObservers() {
        const observeInputs = () => {
            const variantInputs = document.querySelectorAll(`${SELECTORS.FORM} ${SELECTORS.VARIANT_INPUT}`);
            variantInputs.forEach(input => {
                if (input.dataset.observed) return;
                const observer = new MutationObserver((mutations) => {
                    let shouldUpdate = false;
                    mutations.forEach((mutation) => {
                        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
                            shouldUpdate = true;
                        }
                    });
                    if (shouldUpdate) updateButtonStates();
                });
                observer.observe(input, { attributes: true });
                input.dataset.observed = 'true';
            });
        };

        observeInputs();

        let bodyObserverTimer;
        const bodyObserver = new MutationObserver((mutations) => {
            let shouldRecheck = false;
            mutations.forEach(mutation => {
                if (mutation.addedNodes.length) shouldRecheck = true;
            });
            if (shouldRecheck) {
                clearTimeout(bodyObserverTimer);
                bodyObserverTimer = setTimeout(() => observeInputs(), 300);
            }
        });
        bodyObserver.observe(document.body, { childList: true, subtree: true });
    }

    function setupChangeListeners() {
        document.addEventListener('change', (e) => {
            if (e.target.matches(SELECTORS.VARIANT_PICKERS) || e.target.closest(SELECTORS.VARIANT_PICKERS)) {
                setTimeout(updateButtonStates, 100);
            }
        });
    }

    function handleCartRedirect(e) {
        const button = e.target.closest(`.${SELECTORS.IN_CART_CLASS}`);
        if (button) {
            e.preventDefault();
            e.stopPropagation();

            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && typeof cartDrawer.open === 'function') {
                cartDrawer.open(button);
            } else {
                window.location.href = (window.routes && window.routes.cart_url) || '/cart';
            }
        }
    }

    // Coalesces every call made before the next frame into one repaint of all buttons
    let repaintScheduled = false;
    function updateButtonStates() {
        if (repaintScheduled) return;
        repaintScheduled = true;
        requestAnimationFrame(() => {
            repaintScheduled = false;
            paintButtonStates();
        });
    }

    function paintButtonStates() {
        const strings = {
            addToCart: window.variantStrings?.addToCart || 'Add to Cart',
            soldOut: window.variantStrings?.soldOut || 'Sold Out',
            inCart: 'In Your Cart'
        };

        document.querySelectorAll(SELECTORS.BUTTON).forEach(button => {
            const form = button.closest(SELECTORS.FORM);
            let variantId = null;

            if (form) {
                const input = form.querySelector(SELECTORS.VARIANT_INPUT);
                const variantIdInput = input || form.querySelector('.product-variant-id');
                if (variantIdInput) variantId = parseInt(variantIdInput.value);
            } else if (button.getAttribute('form')) {
                const linkedForm = document.getElementById(button.getAttribute('form'));
                if (linkedForm) {
                    const input = linkedForm.querySelector(SELECTORS.VARIANT_INPUT);
                    if (input) variantId = parseInt(input.value);
                }
            }

            if (!variantId || isNaN(variantId)) return;

            const span = button.querySelector('span') || button;

            if (!button.dataset.originalText) {
                const text = span.textContent.trim();
                button.dataset.originalText = text !== strings.inCart && text !== strings.soldOut ? text : strings.addToCart;
            }

            if (cartVariantIds.has(variantId)) {
                button.classList.add(SELECTORS.IN_CART_CLASS);
                if (span.textContent !== strings.inCart) span.textContent = strings.inCart;
                if (button.disabled) {
                    button.removeAttribute('disabled');
                    button.removeAttribute('aria-disabled');
                }
            } else {
                button.classList.remove(SELECTORS.IN_CART_CLASS);
                if (span.textContent === strings.inCart) span.textContent = button.dataset.originalText;
            }
        });
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
